import { useState, useEffect, useMemo } from 'react';
import { BarChart3, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { FloatingPanel } from '../map/FloatingPanel';
import { HealthBadge, type HealthStatus } from './HealthBadge';

// Mock regulator info
const REGULATORS = [
  { id: 1, label: 'Plaza Mayor' },
  { id: 2, label: 'Av. Ejército' },
  { id: 3, label: 'Glorieta Boixareu' },
  { id: 4, label: 'C/ del Trabajo' },
];

interface KPIData {
  availability: number;
  alarmRate: number;
  trend: 'up' | 'down' | 'stable';
  phaseDistribution: Record<string, number>;
  health: HealthStatus;
}

function generateMockKPI(id: number): KPIData {
  // Deterministic-ish based on id, with small random jitter
  const base = id % 10;
  const jitter = () => (Math.random() - 0.5) * 0.4;

  const availability = Math.min(100, Math.max(95, 99.5 - base * 0.3 + jitter()));
  const alarmRate = Math.max(0, (base === 1 ? 0.8 : base === 3 ? 1.2 : 0.3) + jitter());
  const trend = alarmRate > 1 ? 'up' : alarmRate < 0.5 ? 'down' : 'stable';

  const health: HealthStatus =
    availability < 97 || alarmRate > 2 ? 'red' :
    availability < 99 || alarmRate > 1 ? 'yellow' : 'green';

  return {
    availability,
    alarmRate,
    trend,
    phaseDistribution: { A: 43, B: 7, C: 43, D: 7 },
    health,
  };
}

const TrendIcon = ({ trend }: { trend: 'up' | 'down' | 'stable' }) => {
  if (trend === 'up') return <TrendingUp className="w-3 h-3 text-red-400" />;
  if (trend === 'down') return <TrendingDown className="w-3 h-3 text-green-400" />;
  return <Minus className="w-3 h-3 text-gray-500" />;
};

function PhaseBar({ dist }: { dist: Record<string, number> }) {
  const colors: Record<string, string> = {
    A: 'bg-green-500', B: 'bg-amber-500', C: 'bg-blue-500', D: 'bg-amber-500',
  };
  return (
    <div className="flex h-1.5 rounded-full overflow-hidden gap-px">
      {Object.entries(dist).map(([phase, pct]) => (
        <div
          key={phase}
          className={`${colors[phase] || 'bg-gray-500'}`}
          style={{ width: `${pct}%` }}
          title={`Fase ${phase}: ${pct}%`}
        />
      ))}
    </div>
  );
}

/** Hook for header KPI bar — compact data for all regulators */
export function useHeaderKPIs() {
  const [kpis, setKpis] = useState<Record<number, KPIData>>({});

  useEffect(() => {
    const refresh = () => {
      const data: Record<number, KPIData> = {};
      for (const r of REGULATORS) data[r.id] = generateMockKPI(r.id);
      setKpis(data);
    };
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, []);

  const regulators = useMemo(() =>
    REGULATORS.map(r => ({ id: r.id, label: r.label, ...kpis[r.id] }))
      .filter((r): r is typeof r & KPIData => r.availability !== undefined),
    [kpis],
  );

  const globalHealth = useMemo(() => {
    if (regulators.some(k => k.health === 'red')) return 'red' as HealthStatus;
    if (regulators.some(k => k.health === 'yellow')) return 'yellow' as HealthStatus;
    return 'green' as HealthStatus;
  }, [regulators]);

  const avgAvailability = useMemo(() => {
    if (regulators.length === 0) return 0;
    return regulators.reduce((s, k) => s + k.availability, 0) / regulators.length;
  }, [regulators]);

  return { regulators, globalHealth, avgAvailability };
}

export function KPIDashboard() {
  const [period, setPeriod] = useState<'1h' | '24h' | '7d'>('24h');
  const [expanded, setExpanded] = useState(false);
  const [kpis, setKpis] = useState<Record<number, KPIData>>({});

  // Refresh mock KPIs periodically
  useEffect(() => {
    const refresh = () => {
      const data: Record<number, KPIData> = {};
      for (const r of REGULATORS) {
        data[r.id] = generateMockKPI(r.id);
      }
      setKpis(data);
    };
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [period]);

  const globalHealth = useMemo(() => {
    const values = Object.values(kpis);
    if (values.some(k => k.health === 'red')) return 'red' as HealthStatus;
    if (values.some(k => k.health === 'yellow')) return 'yellow' as HealthStatus;
    return 'green' as HealthStatus;
  }, [kpis]);

  const avgAvailability = useMemo(() => {
    const values = Object.values(kpis);
    if (values.length === 0) return 0;
    return values.reduce((s, k) => s + k.availability, 0) / values.length;
  }, [kpis]);

  return (
    <FloatingPanel
      title={`KPIs · ${period}`}
      position="bottom-right"
      defaultCollapsed={false}
      icon={<BarChart3 className="w-3 h-3 text-cyan-400" />}
    >
      <div className="min-w-[260px]">
        {/* Compact view: always visible */}
        <div className="px-3 py-2 space-y-1.5">
          <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
            <span>Regulador</span>
            <span>Disponibilidad</span>
          </div>
          {REGULATORS.map(r => {
            const kpi = kpis[r.id];
            if (!kpi) return null;
            return (
              <div key={r.id} className="flex items-center gap-1.5">
                <HealthBadge status={kpi.health} />
                <span className="text-[11px] font-mono text-white">R{r.id}</span>
                <span className="text-[10px] text-gray-500 truncate">{r.label}</span>
                <span className="text-[11px] text-gray-300 ml-auto font-mono">{kpi.availability.toFixed(1)}%</span>
              </div>
            );
          })}
        </div>

        {/* Expand toggle */}
        <button
          onClick={() => setExpanded(e => !e)}
          className="w-full px-3 py-1 text-[10px] text-gray-500 hover:text-gray-300 transition-colors border-t border-gray-700/30"
        >
          {expanded ? 'Colapsar' : 'Ver detalle'}
        </button>

        {/* Expanded detail */}
        {expanded && (
          <div className="border-t border-gray-700/30">
            {REGULATORS.map(r => {
              const kpi = kpis[r.id];
              if (!kpi) return null;
              return (
                <div key={r.id} className="px-3 py-2 border-b border-gray-700/20 last:border-0">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <HealthBadge status={kpi.health} />
                    <span className="text-[11px] font-mono text-white font-medium">R{r.id}</span>
                    <span className="text-[10px] text-gray-500">{r.label}</span>
                    <span className="text-[11px] text-gray-300 ml-auto">{kpi.availability.toFixed(1)}%</span>
                  </div>
                  <div className="ml-4 space-y-1">
                    <div className="flex items-center gap-1.5 text-[10px]">
                      <TrendIcon trend={kpi.trend} />
                      <span className="text-gray-400">Alarmas: {kpi.alarmRate.toFixed(1)}/h</span>
                    </div>
                    <div>
                      <PhaseBar dist={kpi.phaseDistribution} />
                      <div className="flex justify-between text-[9px] text-gray-600 mt-0.5">
                        {Object.entries(kpi.phaseDistribution).map(([p, pct]) => (
                          <span key={p}>{p} {pct}%</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Period selector */}
            <div className="px-3 py-2 flex items-center gap-1.5">
              <span className="text-[10px] text-gray-500 mr-1">Periodo:</span>
              {(['1h', '24h', '7d'] as const).map(p => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                    period === p
                      ? 'bg-cyan-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>

            {/* Global summary */}
            <div className="px-3 py-2 border-t border-gray-700/30 flex items-center gap-2">
              <HealthBadge status={globalHealth} size={10} />
              <span className="text-[11px] text-gray-300">
                Disponibilidad global: <span className="text-white font-medium">{avgAvailability.toFixed(1)}%</span>
              </span>
            </div>
          </div>
        )}
      </div>
    </FloatingPanel>
  );
}
